import {cn} from '../lib/utils'
import {Icon, type IconName} from './Icon'
import {cva, type VariantProps} from 'class-variance-authority'
import {forwardRef, type ReactNode} from 'react'

/**
 * Figma: https://www.figma.com/design/qS60CfMVFGs07l2G3yfhsB/%F0%9F%92%A0-Components?node-id=16249-8431&m=dev
 */

const listItem = cva('group flex w-full items-center border-solid transition-colors', {
  variants: {
    variant: {
      default: 'border-b border-border py-20',
      avatar: 'border-b border-border py-14',
      bordered: 'rounded-8 border border-border py-20',
    },
    state: {
      default: 'hover:border-border-hover',
      pressed: 'border-border-pressed bg-surface-hover',
      selected: 'border-border-selected',
      disabled: 'cursor-default border-border-disabled bg-surface-secondary',
      focused: 'border-border-focus bg-background',
    },
  },
  defaultVariants: {
    variant: 'default',
    state: 'default',
  },
})

export interface ListItemProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'>,
    VariantProps<typeof listItem> {
  /** Title text - displayed in medium weight */
  title: string
  /** Description text - displayed below title in light weight */
  description?: string
  /** Leading icon name from the icon library */
  leadingIcon?: IconName
  /** Leading avatar element (alternative to icon) */
  leadingAvatar?: ReactNode
  /** Show trailing chevron icon */
  showTrailingIcon?: boolean
  /** Click handler */
  onClick?: () => void
  /** Disabled state */
  disabled?: boolean
}

export const ListItem = forwardRef<HTMLButtonElement, ListItemProps>(
  (
    {
      className,
      variant,
      state,
      title,
      description,
      leadingIcon,
      leadingAvatar,
      showTrailingIcon = true,
      onClick,
      disabled,
      ...props
    },
    ref,
  ) => {
    const effectiveVariant = leadingAvatar ? 'avatar' : variant
    const effectiveState = disabled ? 'disabled' : state

    return (
      <button
        ref={ref}
        type="button"
        className={cn(listItem({variant: effectiveVariant, state: effectiveState}), className)}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        {...props}
      >
        {/* Leading Content and Text Container */}
        <div className="flex flex-1 items-center gap-20">
          {/* Leading Icon */}
          {leadingIcon && (
            <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center">
              <Icon
                name={leadingIcon}
                size={20}
                color={
                  disabled
                    ? 'disabled'
                    : state === 'selected'
                      ? 'selected'
                      : state === 'pressed'
                        ? 'pressed'
                        : 'default'
                }
                className={cn(state === 'disabled' && 'text-text-disabled')}
              />
            </div>
          )}

          {/* Leading Avatar */}
          {leadingAvatar && (
            <div
              className={cn(
                'size-60 flex-shrink-0 overflow-hidden rounded-full bg-background-gray-dark transition-opacity',
                state === 'disabled' && 'opacity-50',
                state !== 'disabled' && 'group-hover:opacity-60',
              )}
            >
              {leadingAvatar}
            </div>
          )}

          {/* Text Container */}
          <div
            className={cn(
              'flex flex-1 flex-col items-start gap-2 pr-20 transition-colors',
              state === 'default' && 'text-text',
              state === 'disabled' && 'text-text-disabled',
              state === 'selected' && 'text-text-selected',
              state === 'pressed' && 'text-text-pressed',
              state !== 'disabled' && 'group-hover:text-text-hover',
            )}
          >
            {/* Title */}
            <div className="text-left">
              <span className="display-block text-base leading-base font-medium">{title}</span>
            </div>

            {/* Description */}
            {description && (
              <span className="display-block w-full text-left text-sm leading-sm font-light">
                {description}
              </span>
            )}
          </div>
        </div>

        {/* Trailing Icon */}
        {showTrailingIcon && (
          <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center">
            <Icon
              name="chevron-right"
              size={20}
              color={
                disabled
                  ? 'disabled'
                  : state === 'selected'
                    ? 'selected'
                    : state === 'pressed'
                      ? 'pressed'
                      : 'default'
              }
              className={cn(
                'transition-colors',
                state === 'disabled' && 'text-text-disabled',
                state !== 'disabled' && 'group-hover:text-icon-hover',
              )}
            />
          </div>
        )}
      </button>
    )
  },
)

ListItem.displayName = 'ListItem'
